import { useEffect, useState } from "react";
import { toast } from "sonner";
import { brl, printAgent, effectivePrice } from "@/lib/api";
import { buildLabelHtml, buildLabelSheetHtml } from "@/lib/label";
import { buildLabelZpl } from "@/lib/labelZpl";
import { Modal } from "@/components/panel/ui";
import { Button, Field, Input } from "@/components/ui-kit";
import { Tag, AlertTriangle } from "lucide-react";

const PAPEL_PADRAO = { width_mm: 60, height_mm: 40 };

/**
 * Impressão de etiqueta de produto.
 *
 * A medida do adesivo NÃO é escolhida aqui: ela vem do perfil "etiqueta" do
 * agente daquela máquina, porque quem sabe o que está na etiquetadora é o
 * computador do balcão — e pode ser diferente do computador do depósito.
 * Perguntar o tamanho na tela obrigaria o operador a saber de cor o papel que
 * está na gaveta do outro andar.
 *
 * A prévia é o mesmo HTML que vai para a impressora, num iframe do tamanho real
 * do adesivo: é o que deixa ver que o nome não coube ANTES de gastar o rolo.
 */
export default function LabelDialog({ product, onClose }) {
  const [variantId, setVariantId] = useState(product.variants?.[0]?.id || "");
  const [qty, setQty] = useState(1);
  const [papel, setPapel] = useState(null);   // null = ainda perguntando ao agente
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    let vivo = true;
    printAgent.profiles()
      .then((p) => {
        if (!vivo) return;
        const etq = p?.label;
        setPapel(etq?.configured
          // `zpl` vem do perfil do agente daquela máquina: é lá que alguém marcou
          // "impressora Zebra". A tela não adivinha o modelo — ela pergunta.
          ? { width_mm: etq.width_mm || 60, height_mm: etq.height_mm || 40, configured: true, zpl: !!etq.zpl }
          : { ...PAPEL_PADRAO, configured: false });
      })
      .catch(() => vivo && setPapel({ ...PAPEL_PADRAO, configured: false, offline: true }));
    return () => { vivo = false; };
  }, []);

  const variant = product.variants?.find((v) => v.id === variantId) || product.variants?.[0];
  const etiqueta = variant && {
    name: product.name,
    variant_label: variant.label,
    price: effectivePrice(product, variant),
    barcode: variant.barcode,
  };

  const html = etiqueta && papel ? buildLabelHtml(etiqueta, papel) : "";

  const imprimir = async () => {
    if (!etiqueta) return;
    setPrinting(true);
    try {
      if (papel?.zpl) {
        /* Zebra: ZPL cru, sem driver no meio. A quantidade vai no `^PQ` — a
           própria impressora repete a etiqueta, o que sai na cadência do papel
           em vez de mandar N trabalhos que avançam e cortam entre cada um. */
        await printAgent.printLabelRaw(buildLabelZpl(etiqueta, papel, qty), {
          label: `Etiqueta ${product.name}`,
        });
      } else {
        await printAgent.printLabel(buildLabelSheetHtml(etiqueta, qty, papel), {
          label: `Etiqueta ${product.name}`,
        });
      }
      toast.success(qty > 1 ? `${qty} etiquetas enviadas` : "Etiqueta enviada para impressão");
      onClose();
    } catch (e) {
      toast.error(e.message || "Falha ao imprimir");
    } finally { setPrinting(false); }
  };

  return (
    <Modal open onClose={onClose} title="Imprimir etiqueta" maxWidth="max-w-lg" testId="label-dialog">
      <div className="space-y-3">
        {papel && !papel.configured && (
          <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800"
            data-testid="label-no-profile">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" aria-hidden />
            <span>
              {papel.offline
                ? <>O agente de impressão não respondeu nesta máquina. A prévia usa {PAPEL_PADRAO.width_mm}×{PAPEL_PADRAO.height_mm}mm.</>
                : <>Nenhuma impressora está configurada no perfil <b>Etiqueta</b>. Abra o app de impressão na bandeja para escolher uma — a prévia usa {PAPEL_PADRAO.width_mm}×{PAPEL_PADRAO.height_mm}mm.</>}
            </span>
          </div>
        )}

        {product.variants?.length > 1 && (
          <Field label="Variação" hint="Cada variação tem o próprio código de barras.">
            <div className="flex flex-wrap gap-1.5">
              {product.variants.map((v) => (
                <button key={v.id} type="button" onClick={() => setVariantId(v.id)}
                  aria-pressed={v.id === variantId} data-testid="label-variant"
                  className={`text-xs rounded-md px-2.5 h-9 border transition-colors ${
                    v.id === variantId ? "border-brand-500 bg-brand-50 text-brand-700 font-semibold"
                      : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  {v.label}
                </button>
              ))}
            </div>
          </Field>
        )}

        {variant && !variant.barcode && (
          <p className="text-xs text-amber-700" data-testid="label-no-barcode">
            Esta variação não tem código de barras cadastrado — a etiqueta sai só com nome e preço.
          </p>
        )}

        {/* Prévia no tamanho real. `srcDoc` porque é exatamente o mesmo HTML que
            vai para a impressora: uma reimplementação em JSX divergiria com o
            tempo e a prévia passaria a mentir. */}
        <Field label="Prévia" hint={papel ? `${papel.width_mm}×${papel.height_mm}mm — tamanho real` : "Carregando…"}>
          <div className="flex justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
            {html ? (
              <iframe title="Prévia da etiqueta" srcDoc={html} data-testid="label-preview"
                style={{ width: `${papel.width_mm}mm`, height: `${papel.height_mm}mm`, border: "1px solid #cbd5e1", background: "#fff" }} />
            ) : <span className="text-xs text-slate-400 py-6">Carregando…</span>}
          </div>
        </Field>

        <div className="flex items-end gap-3">
          <Field label="Quantidade" className="w-32">
            <Input type="number" min="1" max="200" value={qty}
              onChange={(e) => setQty(Math.max(1, Math.min(200, parseInt(e.target.value, 10) || 1)))}
              data-testid="label-qty" mono />
          </Field>
          {variant && (
            <p className="text-xs text-slate-500 pb-2.5">
              {brl(effectivePrice(product, variant))}
              {variant.stock != null && ` · ${variant.stock} em estoque`}
            </p>
          )}
        </div>

        <Button onClick={imprimir} loading={printing} disabled={!etiqueta || !papel}
          icon={Tag} size="lg" block data-testid="label-print">
          {qty > 1 ? `Imprimir ${qty} etiquetas` : "Imprimir etiqueta"}
        </Button>
      </div>
    </Modal>
  );
}
